import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Container,
  Button,
  FormLabel,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import './scss/editMember.scss';

import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// importing icons
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { PermIdentity, Publish } from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';

import { toast } from 'react-toastify';
import Iconify from '../components/iconify';

import { API_BASE_URL } from '../utils/env';


const MemberEditPage = () => {
  const { memberId } = useParams();

  const [memberDetails, setMemberDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);

  const [memberType, setMemberType] = useState([]);
  const [departmentPreference, setDepartmentPreference] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);



  // Function to fetch data from the API
  async function fetchPatientDetailsById(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/Member/GetMemberDetailsByid?memberId=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        setError('Network Error');
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      setError(error.message);
      console.error(error.message);
      return null;
    }
  }

  const { userInfo } = useSelector((state) => state.auth);
  // Decode JWT token to get user information
  const decodedToken = jwtDecode(userInfo.token);

  // Extract the 'id' from the decoded token
  const parishId = decodedToken.parishId;

  useEffect(() => {
    // Fetch the data when the component mounts
    fetchPatientDetailsById(memberId, userInfo.token).then((data) => {
      if (data) {
        const fetchedCountry = data.data.country || '';
        const isNigeria = fetchedCountry === 'Nigeria';

        setMemberDetails(data);
        setSelectedCountry(isNigeria);

        setFormValues({
          lastName: data.data.lastName || '',
          firstName: data.data.firstName || '',
          email: data.data.email || '',
          gender: data.data.gender || '',
          dateOfBirth: data.data.dateOfBirth || '',
          department: data.data.department || '',
          memberType: data.data.memberType || '',
          phoneNumber: data.data.phoneNumber || '',
          nextOfKinPhone: data.data.nextOfKinPhone || '',
          nextOfKin: data.data.nextOfKin || '',
          address: data.data.address || '',
          country: data.data.country || '',
          state: data.data.state || '',
          marriageAnniversary: data.data.marriageAnniversary || '',
          yearJoined: data.data.yearJoined || '',
          biometricId: data.data.biometricId || '',
        });
      } else {
        console.log('Failed to fetch data.');
        setError('Failed to fetch data.');
      }
      // Set loading to false once the data fetching process is complete
      setLoader(false);
    });
  }, [memberId, userInfo.token]);

  function formatDate(inputDate) {
    const date = new Date(inputDate);

    // Extract date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is zero-based
    const day = String(date.getDate()).padStart(2, '0');

    // Format the date components
    const formattedDate = `${day} | ${month} | ${year}`;

    return formattedDate;
  }

  function formatDateOfBirth(inputDate) {
    // Split the input string into day and month components
    const [day, month] = inputDate.split(' | ');

    // Create a new date object (using 2000 as a placeholder year since we ignore it)
    const date = new Date(`2000-${month}-${day}`);

    // Get the month name from the date object
    const monthName = date.toLocaleString('default', { month: 'long' });

    // Get the day component
    const formattedDay = date.getDate();

    // Return only the month and day
    return `${monthName} ${formattedDay}`;
  }


  function extractYearFromDate(inputDate) {
    const date = new Date(inputDate);

    // Extract the year component
    const year = date.getFullYear();

    return year;
  }

  function formatAnniversaryDate(inputDate) {
    // Split the input string into day, month, and year components
    const [day, month, year] = inputDate.split(' | ');

    // Create a new Date object using the parsed components
    const date = new Date(`${month}/${day}/${year}`);

    // Get the month name and day from the Date object
    const monthName = date.toLocaleString('default', { month: 'long' });
    const formattedDay = date.getDate();

    // Concatenate the month name and day into the desired format
    const formattedDate = `${monthName} ${formattedDay}`;

    return formattedDate;
  }

  // Convert date to YYYY-MM-DD format for input
  function convertDateFormat(originalDateString) {
    if (!originalDateString) return ''; // Handle cases where the date is undefined or empty

    const originalDate = new Date(originalDateString);
    const formattedDate = `${originalDate.getFullYear()}-${(originalDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${originalDate.getDate().toString().padStart(2, '0')}`;

    return formattedDate;
  }

  function textFieldYearFormat(inputDate) {
    if (!inputDate) return '';

    const year = new Date(inputDate).getFullYear();
    return year.toString();
  }


  // use effect to grab preference department data
  useEffect(() => {
    fetch(`${API_BASE_URL}/Parish/GetDepartmentByParish?Id=${parishId}`, {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setDepartmentPreference(data.data);
      })
      .catch((error) => {
        toast.error('Error fetching Department preference:', error);
      });
  }, [parishId, userInfo.token]);

  // use effect to grab member type data
  useEffect(() => {
    fetch(`${API_BASE_URL}/Member/GetMemberTypeByParish?Id=${parishId}`, {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setMemberType(data.data);
      })
      .catch((error) => {
        toast.error('Error fetching Member type:', error);
      });
  }, [parishId, userInfo.token]);

  // get all countries on load of page
  useEffect(() => {
    fetch(`${API_BASE_URL}/DropDown/GetAllCountries`, {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setCountries(data);
      })
      .catch((error) => {
        toast.error('Error fetching countries:', error);
      });
  }, [parishId, userInfo.token]);

  // use effect to grab state data below
  useEffect(() => {
    fetch(`${API_BASE_URL}/DropDown/GetAllStates`, {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setStates(data);
      })
      .catch((error) => {
        toast.error('Error fetching states:', error);
      });
  }, [parishId, userInfo.token]);

  const [formValues, setFormValues] = useState({
    memberId: `${memberId}`,
    lastName: '',
    firstName: '',
    email: '',
    gender: '',
    department: '',
    memberType: '',
    phoneNumber: '',
    nextOfKinPhone: '',
    nextOfKin: '',
    address: '',
    country: '',
    state: '',
    dateOfBirth: '',
    marriageAnniversary: '',
    yearJoined: '',
    biometricId: ''
  });

  const handleChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.id]: e.target.value,
    });
  };

  const handleSelectChange = (event) => {
    if (event.target) {
      const { name, value } = event.target;
      setFormValues((prevFormData) => ({ ...prevFormData, [name]: value }));
    }
  };

  const [selectedCountry, setSelectedCountry] = useState(false);


  const handleCountryChange = (event) => {
    const selectedCountryValue = event.target.value;
    const isNigeria = selectedCountryValue === 'Nigeria';

    setSelectedCountry(isNigeria);
    setFormValues((prevFormData) => ({
      ...prevFormData,
      country: selectedCountryValue,
      state: isNigeria ? memberDetails.data.state : '',
    }));
  };


  const handleDateOfBirthChange = (date, fieldName) => {
    if (date) {
      const formattedDate = date.format('YYYY-MM-DDT00:00:00');
      setFormValues((prevFormData) => ({
        ...prevFormData,
        [fieldName]: formattedDate
      }));
      console.log("Formatted Date:", formattedDate);
    }
  };

  const handleMarriageAnniversaryChange = (date) => {
    if (date) {
      const formattedDate = date.format('YYYY-MM-DDT00:00:00'); // Ensure API format
      setFormValues((prevFormData) => ({
        ...prevFormData,
        marriageAnniversary: formattedDate,
      }));
      console.log("Formatted Marriage Anniversary:", formattedDate);
    }
  };


  const handleYearJoinedChange = (date) => {
    if (date) {
      const formattedDate = date.startOf('year').format('YYYY-MM-DDT00:00:00'); // Format as YYYY-MM-DDT00:00:00
      setFormValues((prevFormData) => ({
        ...prevFormData,
        yearJoined: formattedDate, // Store in correct format
      }));
      console.log("Formatted Year:", formattedDate);
    }
  };

  // function to submit the form
  const handleUpdate = async () => {
    const today = dayjs(); // Get today's date

    // Validate phone numbers
    if (!/^\d{11}$/.test(formValues.phoneNumber)) {
      toast.error("Phone number must be exactly 11 digits.");
      return;
    }

    if (!/^\d{11}$/.test(formValues.nextOfKinPhone)) {
      toast.error("Next of kin phone number must be exactly 11 digits.");
      return;
    }

    // Validate Email format
    if (!/^\S+@\S+\.\S+$/.test(formValues.email)) {
      toast.error('Please enter a valid Email address');
      return;
    }

    // Validate Future Dates
    // if (formValues.dateOfBirth && dayjs(formValues.dateOfBirth).isAfter(today)) {
    //   toast.error("Date of birth cannot be in the future.");
    //   return;
    // }

    // if (formValues.marriageAnniversary && dayjs(formValues.marriageAnniversary).isAfter(today)) {
    //   toast.error("Marriage anniversary cannot be in the future.");
    //   return;
    // }

    if (formValues.yearJoined && dayjs(formValues.yearJoined).isAfter(today, 'year')) {
      toast.error("Year joined cannot be in the future.");
      return;
    }

    setIsLoading(true);
    const apiUrl = `${API_BASE_URL}/Member/UpdateMemberDetails`;

    const token = userInfo.token;
    const formData = new FormData();

    formData.append('memberId', memberId);
    formData.append('firstName', formValues.firstName);
    formData.append('lastName', formValues.lastName);
    formData.append('email', formValues.email);
    formData.append('phoneNumber', formValues.phoneNumber);
    formData.append('address', formValues.address);
    formData.append('nextOfKin', formValues.nextOfKin);
    formData.append('nextOfKinPhone', formValues.nextOfKinPhone);
    formData.append('gender', formValues.gender);
    formData.append('department', formValues.department);
    formData.append('memberType', formValues.memberType);
    formData.append('state', formValues.state);
    formData.append('country', formValues.country);
    formData.append('dateOfBirth', formValues.dateOfBirth);
    formData.append('marriageAnniversary', formValues.marriageAnniversary);
    formData.append('yearJoined', formValues.yearJoined);
    formData.append('biometricId', formValues.biometricId);

    // Making the PUT request using Axios
    await axios
      .put(apiUrl, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        const data = response.data;
        if (data.success) {
          setMemberDetails(data);
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      })
      .catch((error) => {
        console.error('Error updating member details:', error);
        toast.error('An error occurred while updating member details.');
      })
      .finally(() => {
        setIsLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };


  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ];

  const value = dayjs(formValues.dateOfBirth).set("year", 0);
  console.log(value);




  return (
    <Container>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Edit Member
          </Typography>
        </Stack>
        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', allignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: '#000080' }} />
          </Box>
        ) : (
          <>
            {error ? (
              <p>Error: {error}</p>
            ) : (
              <Box mt="20px" display={'flex'} gap={'20px'} className="user">
                <div className="userShow">
                  <div className="userShowTop">
                    <Avatar src="./FakePath.png" alt={`${memberDetails.data.lastName}`} className="userShowImg" />
                    <div className="userShowTopTitle">
                      <span className="userShowUsername">{`${memberDetails.data.lastName} ${memberDetails.data.firstName}`}</span>
                      <span className="userShowUserTitle">{`${memberDetails.data.memberType}`}</span>
                    </div>
                  </div>

                  <div className="userShowBottom">
                    {/* account details */}
                    <Box>
                      <span className="userShowTitle">Account Details</span>

                      <div className="userShowInfo">
                        <Iconify className='userShowIcon' icon={'mingcute:department-line'} />
                        <span className="userShowInfoTitle">{`${memberDetails.data.department}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <DateRangeIcon className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${formatDateOfBirth(
                          formatDate(memberDetails.data.dateOfBirth)
                        )}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify
                          className="userShowIcon"
                          icon={memberDetails.data.gender === 'Female' ? 'la:female' : 'fa-solid:male'}
                        />
                        <span className="userShowInfoTitle">{memberDetails.data.gender}</span>
                      </div>

                    </Box>


                    {/* contact details */}
                    <Box>
                      <span className="userShowTitle">Contact Details</span>

                      <div className="userShowInfo">
                        <Iconify className="userShowIcon" icon={'mdi-light:phone'} />
                        <span className="userShowInfoTitle">{`${memberDetails.data.phoneNumber}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'mdi:email-mark-as-unread'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.email}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'mdi:address-marker-outline'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.address}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'mdi:estate'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.state}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'ion:earth'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.country}`}</span>
                      </div>
                    </Box>

                    {/* other details */}
                    <Box>
                      <span className="userShowTitle">Other Details</span>

                      <div className="userShowInfo">
                        <Iconify icon={'wpf:name'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.nextOfKin}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'ant-design:phone-filled'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.nextOfKinPhone}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'uim:calender'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${extractYearFromDate(
                          memberDetails.data.yearJoined
                        )}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'mingcute:anniversary-fill'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${formatAnniversaryDate(
                          formatDate(memberDetails.data.marriageAnniversary)
                        )}`}</span>
                      </div>

                      <div className="userShowInfo">
                        <Iconify icon={'hugeicons:biometric-device'} className="userShowIcon" />
                        <span className="userShowInfoTitle">{`${memberDetails.data.biometricId}`}</span>
                      </div>
                    </Box>

                  </div>
                </div>

                <div className="userUpdate">
                  <span className="userUpdateTitle">Edit</span>
                  <form className="userUpdateForm">
                    <div className="userUpdateLeft">
                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="lastName" className="label">
                          Last Name
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder={formValues.lastName}
                          value={formValues.lastName}
                          onChange={handleChange}
                          className="input"
                          id="lastName"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="firstName" className="label">
                          First Name
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={formValues.firstName}
                          onChange={handleChange}
                          placeholder={formValues.firstName}
                          className="input"
                          id="firstName"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="email" className="label">
                          Email
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          type="email"
                          size="small"
                          value={formValues.email}
                          onChange={handleChange}
                          placeholder="Enter email"
                          className="input"
                          id="email"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="dateOfBirth" className="label">
                          Date of Birth
                        </FormLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            views={['month', 'day']}
                            name='DateOfBirth'
                            value={dayjs(formValues.dateOfBirth)}
                            onChange={(date) => handleDateOfBirthChange(date, 'dateOfBirth')}
                            renderInput={(params) => <TextField {...params} size='small' />}
                            inputFormat="dd/MM"
                            sx={{
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                              '& .MuiInputLabel-outlined.Mui-focused': {
                                color: '#000080',
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </FormControl>


                      <FormControl className="userUpdateItem">
                        <FormLabel id="gender" className="label">
                          Gender
                        </FormLabel>
                        <Select
                          labelId="gender"
                          id="gender"
                          variant="outlined"
                          size="small"
                          className="input"
                          value={formValues.gender}
                          name="gender"
                          onChange={handleSelectChange}
                          sx={{
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                            },
                          }}
                        >
                          {genderOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel id="department" className="label">
                          Department
                        </FormLabel>
                        <Select
                          labelId="department"
                          id="department"
                          variant="outlined"
                          size="small"
                          className="input"
                          value={formValues.department}
                          name="department"
                          onChange={handleSelectChange}
                          sx={{
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                            },
                          }}
                        >
                          {departmentPreference.map((option) => (
                            <MenuItem key={option.id} value={option.departmentName}>
                              {option.departmentName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel id="memberType" className="label">
                          Member type
                        </FormLabel>
                        <Select
                          labelId="memberType"
                          id="memberType"
                          variant="outlined"
                          size="small"
                          className="input"
                          value={formValues.memberType}
                          name="memberType"
                          onChange={handleSelectChange}
                          sx={{
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                            },
                          }}
                        >
                          {memberType.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="phoneNumber" className="label">
                          Phone
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={formValues.phoneNumber}
                          onChange={handleChange}
                          placeholder="Enter phone number"
                          className="input"
                          id="phoneNumber"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="nextOfKin" className="label">
                          Name of next of kin
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          type="text"
                          value={formValues.nextOfKin}
                          onChange={handleChange}
                          placeholder="Enter next of kin name"
                          className="input"
                          id="nextOfKin"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="nextOfKinPhone" className="label">
                          Number of Next of Kin
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          type={'number'}
                          value={formValues.nextOfKinPhone}
                          onChange={handleChange}
                          placeholder="Enter next of kin phone"
                          className="input"
                          id="nextOfKinPhone"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel id="marriageAnniversary" htmlFor="marriageAnniversary" className="label">
                          Marriage Anniversary
                        </FormLabel>
                        {/* <TextField
                          variant="outlined"
                          type="date"
                          size="small"
                          value={convertDateFormat(formValues.marriageAnniversary)}
                          onChange={handleMarriageAnniversaryChange}
                          className="input"
                          id="marriageAnniversary"
                          inputProps={{
                            max: new Date().toISOString().split('T')[0],
                          }}
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        /> */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            views={['month', 'day']}
                            name="marriageAnniversary"
                            value={formValues.marriageAnniversary ? dayjs(formValues.marriageAnniversary) : null}
                            onChange={(date) => handleMarriageAnniversaryChange(date)}
                            maxDate={dayjs()}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            sx={{
                              // change the focus color
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                              // the label color
                              '& .MuiInputLabel-outlined.Mui-focused': {
                                color: '#000080',
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel id="yearJoined" htmlFor="yearJoined" className="label">
                          Year Joined
                        </FormLabel>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            views={['year']}
                            name="yearJoined"
                            value={formValues.yearJoined ? dayjs(formValues.yearJoined, 'YYYY') : null}
                            onChange={(date) => handleYearJoinedChange(date)}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            maxDate={dayjs()}
                            sx={{
                              // change the focus color
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                              // the label color
                              '& .MuiInputLabel-outlined.Mui-focused': {
                                color: '#000080',
                              },
                            }}
                          />
                        </LocalizationProvider>


                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel id="country" className="label">
                          Country
                        </FormLabel>
                        <Select
                          labelId="country"
                          id="country"
                          variant="outlined"
                          size="small"
                          className="input"
                          value={formValues.country}
                          name="country"
                          onChange={handleCountryChange}
                          sx={{
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#000080',
                              },
                            },
                          }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.name}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedCountry && (
                        <FormControl className="userUpdateItem">
                          <FormLabel id="state" className="label">
                            State
                          </FormLabel>
                          <Select
                            labelId="state"
                            id="state"
                            variant="outlined"
                            size="small"
                            className="input"
                            value={formValues.state}
                            name="state"
                            disabled={!selectedCountry}
                            onChange={handleSelectChange}
                            sx={{
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#000080',
                                },
                              },
                            }}
                          >
                            {states.map((state) => (
                              <MenuItem key={state.id} value={state.name}>
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="address" className="label">
                          Address
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Enter Address"
                          className="input"
                          value={formValues.address}
                          onChange={handleChange}
                          id="address"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>

                      <FormControl className="userUpdateItem">
                        <FormLabel htmlFor="biometricId" className="label">
                          Biometric ID
                        </FormLabel>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Biometric ID"
                          className="input"
                          value={formValues.biometricId}
                          onChange={handleChange}
                          id="biometricId"
                          sx={{
                            // change the focus color
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#000080',
                            },
                          }}
                        />
                      </FormControl>
                    </div>

                    <div className="userUpdateRight">
                      <div className="userUpdateUpload">
                        <Avatar src={'profile'} alt="Tunde Amos" className="userUpdateImg" />
                        <FormLabel htmlFor="file" className="label">
                          {' '}
                          <Publish className="userUpdateIcon" />{' '}
                        </FormLabel>
                        <input type="file" id="file" style={{ display: 'none' }} />
                      </div>

                      <Button
                        disabled={isLoading}
                        variant="contained"
                        sx={{ background: '#000080' }}
                        className="userUpdateButton"
                        startIcon={<PersonAddAlt1Icon />}
                        onClick={handleUpdate}
                      >
                        {isLoading ? 'Updating...' : 'Update'}
                      </Button>
                    </div>
                  </form>
                </div>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default MemberEditPage;